import { HamburgerIcon, InfoOutlineIcon, ViewIcon } from "@chakra-ui/icons"
import {
  Badge,
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  Table,
  TableContainer,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router"
import { useDeferredValue, useMemo, useState } from "react"
import Select from "@/components/Common/AppSelect"
import { InventoryCountApi } from "../../../../api/inventoryCount"
import { UsersService, WareHouseService } from "../../../../client"
import { WmsListToolbar } from "../../../../components/WMS/shared/WmsListToolbar"
import { WmsLocationFilterInput } from "../../../../components/WMS/shared/WmsLocationFilterInput"
import { WmsPageHeader } from "../../../../components/WMS/shared/WmsPageHeader"
import {
  WmsPagePanel,
  WmsPageShell,
} from "../../../../components/WMS/shared/WmsPageShell"
import { WmsTableListCard } from "../../../../components/WMS/shared/WmsTableListCard"

export const Route = createFileRoute("/_layout/WMS/inventory-count/tasks")({
  component: InventoryCountTasksRoute,
})

function getTaskStatusColorScheme(status: string): string {
  switch (status) {
    case "open":
      return "gray"
    case "in_progress":
      return "blue"
    case "pending_review":
      return "orange"
    case "approved":
      return "teal"
    case "posted":
      return "green"
    case "cancelled":
      return "red"
    default:
      return "gray"
  }
}

function formatScopeType(scopeType: string): string {
  switch (scopeType) {
    case "item_location":
      return "SKU + lokacja"
    default:
      return scopeType || "—"
  }
}

function formatDueAtDisplay(
  dueAt?: string,
  createdAt?: string,
): {
  text: string
  isEstimated: boolean
} {
  if (dueAt) {
    return { text: new Date(dueAt).toLocaleString(), isEstimated: false }
  }
  if (createdAt) {
    const createdMs = new Date(createdAt).getTime()
    if (Number.isFinite(createdMs)) {
      return {
        text: new Date(createdMs + 24 * 60 * 60 * 1000).toLocaleString(),
        isEstimated: true,
      }
    }
  }
  return { text: "Bez terminu", isEstimated: false }
}

function extractBlockedLocationCode(errorText: string): string {
  const match = errorText.match(
    /location\s+([^\s]+)\s+is locked by another active inventory task/i,
  )
  return match?.[1]?.trim().toUpperCase() ?? ""
}

function InventoryCountTasksRoute() {
  const location = useLocation()
  const isDetailRoute = location.pathname.startsWith(
    "/WMS/inventory-count/tasks/",
  )
  if (isDetailRoute) {
    return <Outlet />
  }

  return <InventoryCountTasksPage />
}

function InventoryCountTasksPage() {
  const qc = useQueryClient()
  const [warehouseId, setWarehouseId] = useState("")
  const [status, setStatus] = useState("")
  const [locationFilter, setLocationFilter] = useState("")
  const deferredLocationFilter = useDeferredValue(locationFilter)
  const [myTasks, setMyTasks] = useState(false)
  const [blockingDraftDocs, setBlockingDraftDocs] = useState<
    Array<{
      document_type: string
      document_id: string
      document_no: string
      status: string
      created_at: string
    }>
  >([])
  const [blockingLocks, setBlockingLocks] = useState<
    Array<{
      id: string
      task_id: string
      task_no: string
      task_status: string
      warehouse_id: string
      location_code: string
      locked_by_id: string
      locked_at: string
    }>
  >([])
  const [isBlockingModalOpen, setBlockingModalOpen] = useState(false)
  const [blockingDetailsLoading, setBlockingDetailsLoading] = useState(false)
  const [blockingErrorText, setBlockingErrorText] = useState("")
  const [blockingWarehouseId, setBlockingWarehouseId] = useState("")
  const [blockingLocationCode, setBlockingLocationCode] = useState("")
  const [isReadyToPostModalOpen, setReadyToPostModalOpen] = useState(false)
  const toast = useToast()

  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const theadBg = useColorModeValue("gray.100", "whiteAlpha.100")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")
  const mutedText = useColorModeValue("gray.500", "gray.400")

  const warehousesQuery = useQuery({
    queryKey: ["wms", "warehouses", "list", { includeArchived: false }],
    queryFn: () => WareHouseService.getAllWarehouses(false),
    retry: false,
  })
  const usersQuery = useQuery({
    queryKey: ["users", "list", "inventory-count-tasks"],
    queryFn: () => UsersService.readUsers({ limit: 500, skip: 0 }),
    retry: false,
  })
  const blockingWarehouseLabel = useMemo(() => {
    if (!blockingWarehouseId) {
      return "—"
    }
    const warehouse = warehousesQuery.data?.find(
      (item) => item.id === blockingWarehouseId,
    )
    if (!warehouse) {
      return blockingWarehouseId
    }
    return `${warehouse.code} - ${warehouse.name}`
  }, [blockingWarehouseId, warehousesQuery.data])

  const tasksQuery = useQuery({
    queryKey: [
      "wms",
      "inventory-count",
      "tasks",
      { warehouseId, status, myTasks, location_code: deferredLocationFilter },
    ],
    retry: false,
    queryFn: () =>
      InventoryCountApi.getTasks({
        warehouse_id: warehouseId || undefined,
        status: status || undefined,
        my_tasks: myTasks,
        location_code: deferredLocationFilter || undefined,
        page: 1,
        page_size: 100,
      }),
  })
  const locksQuery = useQuery({
    queryKey: [
      "wms",
      "inventory-count",
      "locks",
      { warehouseId, location_code: deferredLocationFilter },
    ],
    retry: false,
    queryFn: () =>
      InventoryCountApi.getLocationLocks({
        warehouse_id: warehouseId || undefined,
        location_code: deferredLocationFilter || undefined,
        page: 1,
        page_size: 100,
      }),
  })
  const readyToPostQuery = useQuery({
    queryKey: [
      "wms",
      "inventory-count",
      "tasks",
      "ready-to-post",
      { warehouseId, myTasks, location_code: deferredLocationFilter },
    ],
    retry: false,
    queryFn: () =>
      InventoryCountApi.getTasks({
        warehouse_id: warehouseId || undefined,
        status: "approved",
        my_tasks: myTasks,
        location_code: deferredLocationFilter || undefined,
        page: 1,
        page_size: 10,
      }),
  })

  const startTaskMutation = useMutation({
    mutationFn: (payload: { taskId: string }) =>
      InventoryCountApi.startTask(payload.taskId),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
    },
  })
  const postTaskMutation = useMutation({
    mutationFn: (payload: { taskId: string }) =>
      InventoryCountApi.postTask(payload.taskId),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "locks"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "kpi"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "alerts"],
      })
      toast({
        title: "Inwentaryzacja została zaksięgowana",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    },
  })
  const assignTaskMutation = useMutation({
    mutationFn: (payload: { taskId: string; assignedToUserId?: string }) =>
      InventoryCountApi.assignTask(payload.taskId, payload.assignedToUserId),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
      toast({
        title: "Przypisanie zadania zostało zapisane",
        status: "success",
        duration: 2500,
        isClosable: true,
      })
    },
    onError: (err: any) => {
      const errorText =
        err?.body?.error ??
        err?.message ??
        "Nie udało się przypisać zadania inwentaryzacji."
      toast({
        title: "Błąd przypisania zadania",
        description: String(errorText),
        status: "error",
        duration: 4500,
        isClosable: true,
      })
    },
  })

  const rows = tasksQuery.data?.items ?? []
  const filteredRows = rows
  const filteredReadyToPostRows = readyToPostQuery.data?.items ?? []
  const filteredLocks = locksQuery.data?.items ?? []

  const blockingLocksForLocation = useMemo(() => {
    const normalized = blockingLocationCode.trim().toUpperCase()
    if (!normalized) {
      return blockingLocks
    }
    return blockingLocks.filter(
      (lock) => lock.location_code.trim().toUpperCase() === normalized,
    )
  }, [blockingLocationCode, blockingLocks])

  const userNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const user of usersQuery.data?.data ?? []) {
      const id = String(user.id ?? user.ID ?? "").trim()
      if (!id) {
        continue
      }
      const name = String(user.fullName ?? "").trim()
      map.set(id, name || id)
    }
    return map
  }, [usersQuery.data])
  const userAcronymById = useMemo(() => {
    const map = new Map<string, string>()
    for (const user of usersQuery.data?.data ?? []) {
      const id = String(user.id ?? user.ID ?? "").trim()
      if (!id) {
        continue
      }
      const acronym = String(user.acronym ?? "").trim()
      if (acronym) {
        map.set(id, acronym)
      }
    }
    return map
  }, [usersQuery.data])
  const userOptions = useMemo(() => {
    return (usersQuery.data?.data ?? [])
      .map((user) => {
        const id = String(user.id ?? user.ID ?? "").trim()
        if (!id) {
          return null
        }
        const fullName = String(user.fullName ?? "").trim()
        const email = String(user.email ?? "").trim()
        const acronym = String(user.acronym ?? "").trim()
        return {
          id,
          label:
            fullName ||
            [acronym, email].filter((part) => part.length > 0).join(" · ") ||
            id,
        }
      })
      .filter((user): user is { id: string; label: string } => user !== null)
      .sort((a, b) => a.label.localeCompare(b.label, "pl"))
  }, [usersQuery.data])

  const getDocumentLink = (documentType: string, documentID: string) => {
    switch (documentType) {
      case "WZ":
        return `/WMS/documents/ex-releases/${documentID}`
      case "PZ":
        return `/WMS/documents/ex-receptions/${documentID}`
      case "PW":
        return `/WMS/documents/doc-pw/${documentID}`
      case "RW":
        return `/WMS/documents/doc-rw/${documentID}`
      case "MMW":
        return `/WMS/documents/doc-mmw/${documentID}`
      case "MMP":
        return `/WMS/documents/doc-mmp/${documentID}`
      case "MMR":
        return `/WMS/documents/doc-mmr/${documentID}`
      default:
        return ""
    }
  }

  const tryStartTask = async (taskId: string, warehouseID: string) => {
    try {
      await startTaskMutation.mutateAsync({ taskId })
    } catch (err: any) {
      const errorText =
        err?.body?.error ??
        err?.message ??
        "Nie udało się rozpocząć zadania inwentaryzacji."
      setBlockingErrorText(errorText)
      setBlockingWarehouseId(warehouseID)
      setBlockingLocationCode(extractBlockedLocationCode(errorText))
      setBlockingDetailsLoading(true)
      setBlockingModalOpen(true)

      const [locksResult, draftsResult] = await Promise.allSettled([
        InventoryCountApi.getLocationLocks({
          warehouse_id: warehouseID,
          page: 1,
          page_size: 200,
        }),
        InventoryCountApi.getBlockingDraftDocuments({
          warehouse_id: warehouseID,
          limit: 200,
        }),
      ])
      setBlockingLocks(
        locksResult.status === "fulfilled"
          ? (locksResult.value.items ?? [])
          : [],
      )
      setBlockingDraftDocs(
        draftsResult.status === "fulfilled"
          ? (draftsResult.value.items ?? [])
          : [],
      )
      setBlockingDetailsLoading(false)

      if (!String(errorText).includes("Nie można rozpocząć inwentaryzacji")) {
        toast({
          title: "Nie udało się rozpocząć zadania",
          description: errorText,
          status: "error",
          duration: 4500,
          isClosable: true,
        })
      }
    }
  }

  return (
    <WmsPageShell>
      <WmsPageHeader
        title="Kolejka zadań inwentaryzacji"
        description="Kolejka zadań inwentaryzacyjnych z podstawową obsługą startu wykonania."
        gradient="linear(to-r, teal.600, blue.600)"
        badge={`Zadania: ${filteredRows.length}`}
      />

      <WmsPagePanel bg={panelBg} borderColor={panelBorder}>
        <Tabs
          variant="enclosed"
          colorScheme="blue"
          display="flex"
          flexDirection="column"
          flex="1"
          minH={0}
        >
          <TabList px={4} pt={4}>
            <Tab>Zadania ({filteredRows.length})</Tab>
            <Tab>
              Aktywne blokady lokacji
              <Badge ml={2} colorScheme="purple">
                {filteredLocks.length}
              </Badge>
            </Tab>
          </TabList>
          <TabPanels
            flex="1"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            <TabPanel
              flex="1"
              display="flex"
              flexDirection="column"
              p={4}
              minH={0}
            >
              <WmsListToolbar
                actionsWrap="nowrap"
                filters={
                  <>
                    <Select
                      size="sm"
                      maxW={{ base: "100%", lg: "260px" }}
                      value={warehouseId}
                      onChange={(event) => setWarehouseId(event.target.value)}
                      borderRadius="xl"
                    >
                      <option value="">Wszystkie magazyny</option>
                      {(warehousesQuery.data ?? []).map((warehouse) => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.code} - {warehouse.name}
                        </option>
                      ))}
                    </Select>
                    <Select
                      size="sm"
                      maxW={{ base: "100%", lg: "220px" }}
                      value={status}
                      onChange={(event) => setStatus(event.target.value)}
                      borderRadius="xl"
                    >
                      <option value="">Wszystkie statusy</option>
                      <option value="open">open</option>
                      <option value="in_progress">in_progress</option>
                      <option value="pending_review">
                        Oczekujące na weryfikację
                      </option>
                      <option value="approved">approved</option>
                      <option value="posted">posted</option>
                      <option value="cancelled">cancelled</option>
                    </Select>
                    <Checkbox
                      size="sm"
                      isChecked={myTasks}
                      onChange={(event) => setMyTasks(event.target.checked)}
                      whiteSpace="nowrap"
                    >
                      Moje zadania
                    </Checkbox>
                    <WmsLocationFilterInput
                      value={locationFilter}
                      onChange={setLocationFilter}
                    />
                  </>
                }
                actions={
                  <HStack spacing={2} wrap="nowrap">
                    <Tooltip
                      label="Kolejka weryfikacji: zadania oczekujące na decyzję approve/recount/reject."
                      hasArrow
                      placement="top"
                    >
                      <IconButton
                        size="sm"
                        as={Link}
                        to="/WMS/inventory-count/review"
                        aria-label="Kolejka weryfikacji"
                        icon={<HamburgerIcon />}
                        variant="outline"
                      />
                    </Tooltip>
                    <Tooltip
                      label={`Ready to post (approved): ${filteredReadyToPostRows.length}`}
                      hasArrow
                      placement="top"
                    >
                      <IconButton
                        size="sm"
                        aria-label="Ready to post (approved)"
                        icon={<ViewIcon />}
                        variant="outline"
                        onClick={() => setReadyToPostModalOpen(true)}
                      />
                    </Tooltip>
                    <Tooltip
                      label="Instrukcja operatora"
                      hasArrow
                      placement="top"
                    >
                      <IconButton
                        size="sm"
                        as={Link}
                        to="/WMS/inventory-count/instructions-operator"
                        aria-label="Instrukcja operatora"
                        icon={<InfoOutlineIcon />}
                        variant="outline"
                      />
                    </Tooltip>
                  </HStack>
                }
              />

              <Box flex="1" minH={0}>
                <WmsTableListCard
                  isLoading={tasksQuery.isPending}
                  isFetching={tasksQuery.isFetching}
                  error={tasksQuery.isError ? tasksQuery.error : undefined}
                  errorMessage="Nie udało się pobrać kolejki zadań inwentaryzacji."
                  isEmpty={
                    !tasksQuery.isPending &&
                    !tasksQuery.isError &&
                    filteredRows.length === 0
                  }
                  emptyMessage="Brak zadań dla wybranych filtrów."
                  minEmptyHeight={{ base: "200px", lg: "320px" }}
                >
                  <TableContainer maxH="62vh" overflowY="auto" overflowX="auto">
                    <Table
                      size="sm"
                      sx={{ tableLayout: "fixed", minW: "1380px" }}
                    >
                      <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                        <Tr>
                          <Th w="240px">Nr zadania</Th>
                          <Th w="180px">Status</Th>
                          <Th w="140px">Zakres</Th>
                          <Th w="110px" isNumeric>
                            Priorytet
                          </Th>
                          <Th w="220px">Przypisano do</Th>
                          <Th w="180px">Termin</Th>
                          <Th w="190px">Utworzono</Th>
                          <Th w="240px">Akcje</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredRows.map((row, idx) => (
                          <Tr
                            key={row.id}
                            bg={idx % 2 === 0 ? "transparent" : zebraBg}
                          >
                            <Td>
                              <Text noOfLines={1} title={row.task_no}>
                                {row.task_no}
                              </Text>
                            </Td>
                            <Td>
                              <Badge
                                whiteSpace="nowrap"
                                colorScheme={getTaskStatusColorScheme(
                                  row.status,
                                )}
                              >
                                {row.status}
                              </Badge>
                            </Td>
                            <Td>{formatScopeType(row.scope_type)}</Td>
                            <Td isNumeric>{row.priority}</Td>
                            <Td>
                              <Select
                                size="xs"
                                value={row.assigned_to_user_id ?? ""}
                                isDisabled={
                                  usersQuery.isPending ||
                                  assignTaskMutation.isPending ||
                                  row.status === "posted" ||
                                  row.status === "cancelled"
                                }
                                title={
                                  row.assigned_to_user_id
                                    ? (userNameById.get(
                                        row.assigned_to_user_id,
                                      ) ?? row.assigned_to_user_id)
                                    : "Nieprzypisane"
                                }
                                onChange={(event) => {
                                  assignTaskMutation.mutate({
                                    taskId: row.id,
                                    assignedToUserId:
                                      event.target.value || undefined,
                                  })
                                }}
                              >
                                <option value="">Nieprzypisane</option>
                                {userOptions.map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {user.label}
                                  </option>
                                ))}
                              </Select>
                              {row.assigned_to_user_id &&
                              !userNameById.has(row.assigned_to_user_id) ? (
                                <Text
                                  mt={1}
                                  color={mutedText}
                                  fontSize="xs"
                                  noOfLines={1}
                                >
                                  {row.assigned_to_user_id}
                                </Text>
                              ) : null}
                            </Td>
                            <Td>
                              {(() => {
                                const due = formatDueAtDisplay(
                                  row.due_at,
                                  row.created_at,
                                )
                                if (!due.isEstimated) {
                                  return due.text === "Bez terminu" ? (
                                    <Text color={mutedText}>{due.text}</Text>
                                  ) : (
                                    due.text
                                  )
                                }
                                return (
                                  <Tooltip
                                    label="Termin oszacowany: utworzono + 24h"
                                    hasArrow
                                  >
                                    <Text color={mutedText}>{due.text}</Text>
                                  </Tooltip>
                                )
                              })()}
                            </Td>
                            <Td>{new Date(row.created_at).toLocaleString()}</Td>
                            <Td>
                              <Button
                                as="a"
                                size="xs"
                                mr={2}
                                href={`/WMS/inventory-count/tasks/${row.id}`}
                                variant="outline"
                              >
                                Otwórz
                              </Button>
                              {row.status === "open" ? (
                                <Button
                                  size="xs"
                                  colorScheme="blue"
                                  isLoading={
                                    startTaskMutation.isPending &&
                                    startTaskMutation.variables?.taskId ===
                                      row.id
                                  }
                                  onClick={() =>
                                    void tryStartTask(row.id, row.warehouse_id)
                                  }
                                >
                                  Rozpocznij
                                </Button>
                              ) : null}
                              {row.status === "approved" ? (
                                <Button
                                  size="xs"
                                  colorScheme="purple"
                                  isLoading={
                                    postTaskMutation.isPending &&
                                    postTaskMutation.variables?.taskId ===
                                      row.id
                                  }
                                  onClick={async () => {
                                    try {
                                      await postTaskMutation.mutateAsync({
                                        taskId: row.id,
                                      })
                                    } catch (err: any) {
                                      const errorText =
                                        err?.body?.error ??
                                        err?.message ??
                                        "Nie udało się zaksięgować inwentaryzacji."
                                      toast({
                                        title:
                                          "Błąd księgowania inwentaryzacji",
                                        description: String(errorText),
                                        status: "error",
                                        duration: 4500,
                                        isClosable: true,
                                      })
                                    }
                                  }}
                                >
                                  Zaksięguj
                                </Button>
                              ) : null}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </WmsTableListCard>
              </Box>
            </TabPanel>

            <TabPanel
              flex="1"
              display="flex"
              flexDirection="column"
              p={4}
              minH={0}
            >
              <WmsTableListCard
                isLoading={locksQuery.isPending}
                isFetching={locksQuery.isFetching}
                error={locksQuery.isError ? locksQuery.error : undefined}
                errorMessage="Nie udało się pobrać aktywnych blokad lokacji."
                isEmpty={
                  !locksQuery.isPending &&
                  !locksQuery.isError &&
                  filteredLocks.length === 0
                }
                emptyMessage="Brak aktywnych blokad lokacji."
                minEmptyHeight={{ base: "140px", lg: "180px" }}
              >
                <TableContainer maxH="62vh" overflowY="auto">
                  <Table size="sm">
                    <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th>Nr zadania</Th>
                        <Th>Status</Th>
                        <Th>Lokacja</Th>
                        <Th>Zablokowane przez</Th>
                        <Th>Zablokowano</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredLocks.map((lock, idx) => (
                        <Tr
                          key={lock.id}
                          bg={idx % 2 === 0 ? "transparent" : zebraBg}
                        >
                          <Td>{lock.task_no}</Td>
                          <Td>
                            <Badge
                              colorScheme={getTaskStatusColorScheme(
                                lock.task_status,
                              )}
                            >
                              {lock.task_status}
                            </Badge>
                          </Td>
                          <Td>{lock.location_code}</Td>
                          <Td>
                            {userAcronymById.get(lock.locked_by_id) ??
                              userNameById.get(lock.locked_by_id) ??
                              lock.locked_by_id}
                          </Td>
                          <Td>{new Date(lock.locked_at).toLocaleString()}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </WmsTableListCard>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </WmsPagePanel>
      <Modal
        isOpen={isReadyToPostModalOpen}
        onClose={() => setReadyToPostModalOpen(false)}
        size="4xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Text>Ready to post (approved)</Text>
              <Badge colorScheme="purple">
                {filteredReadyToPostRows.length}
              </Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {readyToPostQuery.isPending ? (
              <Box py={8} textAlign="center">
                Ładowanie listy zadań...
              </Box>
            ) : filteredReadyToPostRows.length === 0 ? (
              <Text color={mutedText}>
                Brak zadań approved do zaksięgowania dla wybranych filtrów.
              </Text>
            ) : (
              <TableContainer
                maxH="56vh"
                overflowY="auto"
                borderWidth="1px"
                borderRadius="md"
              >
                <Table size="sm">
                  <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th>Nr zadania</Th>
                      <Th>Status</Th>
                      <Th>Utworzono</Th>
                      <Th>Akcje</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredReadyToPostRows.map((task) => (
                      <Tr key={task.id}>
                        <Td>{task.task_no}</Td>
                        <Td>
                          <Badge
                            colorScheme={getTaskStatusColorScheme(task.status)}
                          >
                            {task.status}
                          </Badge>
                        </Td>
                        <Td>{new Date(task.created_at).toLocaleString()}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Button
                              as="a"
                              href={`/WMS/inventory-count/tasks/${task.id}`}
                              size="xs"
                              variant="outline"
                            >
                              Otwórz
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="purple"
                              isLoading={
                                postTaskMutation.isPending &&
                                postTaskMutation.variables?.taskId === task.id
                              }
                              onClick={async () => {
                                try {
                                  await postTaskMutation.mutateAsync({
                                    taskId: task.id,
                                  })
                                  await readyToPostQuery.refetch()
                                } catch (err: any) {
                                  const errorText =
                                    err?.body?.error ??
                                    err?.message ??
                                    "Nie udało się zaksięgować inwentaryzacji."
                                  toast({
                                    title: "Błąd księgowania inwentaryzacji",
                                    description: String(errorText),
                                    status: "error",
                                    duration: 4500,
                                    isClosable: true,
                                  })
                                }
                              }}
                            >
                              Zaksięguj
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={() => setReadyToPostModalOpen(false)}>
              Zamknij
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isBlockingModalOpen}
        onClose={() => setBlockingModalOpen(false)}
        size="4xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Nie można rozpocząć inwentaryzacji</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3}>
              {blockingErrorText ||
                "W magazynie istnieją dokumenty robocze, które blokują start inwentaryzacji."}
            </Text>
            <Text fontSize="sm" color={mutedText} mb={3}>
              Magazyn: {blockingWarehouseLabel}
            </Text>
            {blockingLocationCode ? (
              <Text fontSize="sm" color={mutedText} mb={3}>
                Blokowana lokacja: {blockingLocationCode}
              </Text>
            ) : null}
            {blockingLocksForLocation.length > 0 ? (
              <Box mb={4} p={3} borderWidth="1px" borderRadius="md">
                <HStack justify="space-between" align="center" mb={2}>
                  <Text fontWeight="semibold">
                    Blokujące zadanie inwentaryzacji
                  </Text>
                  <Badge colorScheme="red">
                    {blockingLocksForLocation.length}
                  </Badge>
                </HStack>
                <TableContainer overflowX="auto">
                  <Table size="sm">
                    <Thead bg={theadBg}>
                      <Tr>
                        <Th>Nr zadania</Th>
                        <Th>Status</Th>
                        <Th>Lokacja</Th>
                        <Th>Zablokowane przez</Th>
                        <Th>Zablokowano</Th>
                        <Th>Akcja</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {blockingLocksForLocation.map((lock) => (
                        <Tr key={lock.id}>
                          <Td>{lock.task_no}</Td>
                          <Td>
                            <Badge
                              colorScheme={getTaskStatusColorScheme(
                                lock.task_status,
                              )}
                            >
                              {lock.task_status}
                            </Badge>
                          </Td>
                          <Td>{lock.location_code}</Td>
                          <Td>
                            {userAcronymById.get(lock.locked_by_id) ??
                              userNameById.get(lock.locked_by_id) ??
                              lock.locked_by_id}
                          </Td>
                          <Td>{new Date(lock.locked_at).toLocaleString()}</Td>
                          <Td>
                            <Button
                              as="a"
                              href={`/WMS/inventory-count/tasks/${lock.task_id}`}
                              size="xs"
                              variant="outline"
                            >
                              Otwórz zadanie
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            ) : null}
            {blockingLocks.length > 0 && blockingLocationCode ? (
              <Text fontSize="sm" color={mutedText} mb={3}>
                Nie udało się dopasować blokady do lokacji z błędu, pokazuję
                wszystkie blokady dla tego magazynu.
              </Text>
            ) : null}
            {blockingDetailsLoading ? (
              <Text>Ładowanie listy dokumentów...</Text>
            ) : (
              <TableContainer
                maxH="48vh"
                overflowY="auto"
                borderWidth="1px"
                borderRadius="md"
              >
                <Table size="sm">
                  <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th>Typ</Th>
                      <Th>Numer</Th>
                      <Th>Status</Th>
                      <Th>Utworzono</Th>
                      <Th>Akcja</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {blockingDraftDocs.length === 0 ? (
                      <Tr>
                        <Td colSpan={5}>
                          <Text color={mutedText}>
                            Brak szkiców dokumentów blokujących start lub brak
                            dostępu do listy.
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      blockingDraftDocs.map((doc) => {
                        const link = getDocumentLink(
                          doc.document_type,
                          doc.document_id,
                        )
                        return (
                          <Tr key={`${doc.document_type}-${doc.document_id}`}>
                            <Td>{doc.document_type}</Td>
                            <Td>{doc.document_no || doc.document_id}</Td>
                            <Td>
                              <Badge colorScheme="orange">{doc.status}</Badge>
                            </Td>
                            <Td>{new Date(doc.created_at).toLocaleString()}</Td>
                            <Td>
                              {link ? (
                                <Button
                                  as="a"
                                  href={link}
                                  size="xs"
                                  variant="outline"
                                >
                                  Otwórz dokument
                                </Button>
                              ) : (
                                <Text color={mutedText}>—</Text>
                              )}
                            </Td>
                          </Tr>
                        )
                      })
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={() => setBlockingModalOpen(false)}>
              Zamknij
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </WmsPageShell>
  )
}
